/**
 * ============================================================================
 * Delivery charge system — client-side helpers
 * ============================================================================
 * The CART total is calculated server-side in Liquid (snippets/delivery-charge
 * .liquid) and refreshes automatically via the theme's Section Rendering, so
 * this file does NOT recalculate the cart. It is responsible for:
 *
 *   1. A reusable, centralised calculation API (window.DeliveryCharge) that
 *      mirrors the Liquid rules exactly — handy for testing and future reuse.
 *   2. The <delivery-estimate> custom element used on the product page, which
 *      shows a live "delivery from" figure and updates on quantity / variant
 *      changes with no page reload.
 *
 * Money note: theme prices are in the shop's minor units (øre / cents). Theme
 * settings are entered in kr, so kr values are multiplied by 100 before being
 * compared with line prices.
 * ============================================================================
 */
(() => {
  const MINOR_UNITS = 100; // kr -> øre

  /**
   * Parse the shared registry text produced by snippets/delivery-data.liquid.
   * Format: "handle|charge_kr|free_above_kr|label;handle|...".
   * @returns {Object<string,{charge:number, freeAbove:number, label:string}>}
   */
  function parseData(text) {
    const map = {};
    if (!text) return map;

    text
      .split(';')
      .map((row) => row.trim())
      .filter(Boolean)
      .forEach((row) => {
        const [handle, charge, freeAbove, label] = row.split('|');
        if (!handle) return;
        map[handle.trim()] = {
          charge: Number(charge) || 0,
          freeAbove: Number(freeAbove) || 0,
          label: (label || handle).trim(),
        };
      });

    return map;
  }

  /**
   * Central calculation — mirrors the Liquid rules.
   *
   * @param {Array<{profile:string, cents:number}>} items - per line item: its
   *        profile handle and its line total in minor units (cents).
   * @param {Object} profiles - registry map from parseData().
   * @param {string} [defaultProfile='diverse'] - fallback for unmapped items.
   * @returns {number} the single delivery fee in minor units (0 = free).
   */
  function calculate(items, profiles, defaultProfile = 'diverse') {
    if (!items || !items.length) return 0;

    // 1. Sum line totals per profile.
    const subtotals = {};
    items.forEach(({ profile, cents }) => {
      const key = profiles[profile] ? profile : defaultProfile;
      subtotals[key] = (subtotals[key] || 0) + (Number(cents) || 0);
    });

    // 2. Apply the rules over the profiles actually present.
    let anyFree = false;
    let maxChargeCents = 0;

    Object.keys(subtotals).forEach((handle) => {
      const config = profiles[handle];
      if (!config) return;

      const subtotal = subtotals[handle];
      if (subtotal <= 0) return;

      const freeCents = config.freeAbove * MINOR_UNITS;
      if (config.freeAbove > 0 && subtotal >= freeCents) {
        anyFree = true; // Rule 1 / Rule 3: any profile free -> whole cart free.
      }

      const chargeCents = config.charge * MINOR_UNITS;
      if (chargeCents > maxChargeCents) {
        maxChargeCents = chargeCents; // Rule 2: highest single charge.
      }
    });

    // Rule 3 overrides everything; otherwise the single highest charge.
    return anyFree ? 0 : maxChargeCents;
  }

  function formatMoney(cents, currency) {
    return new Intl.NumberFormat(document.documentElement.lang || 'da-DK', {
      style: 'currency',
      currency: currency || window.Shopify?.currency?.active || 'DKK',
      minimumFractionDigits: 2,
    }).format(cents / MINOR_UNITS);
  }

  // Read the site-wide registry once (rendered in layout/theme.liquid).
  function readGlobalProfiles() {
    const node = document.getElementById('DeliveryData');
    return parseData(node ? node.textContent : '');
  }

  // Public API — reusable + testable.
  window.DeliveryCharge = { parseData, calculate, formatMoney, readGlobalProfiles };

  /**
   * <delivery-estimate> — product page single-product estimate.
   * Reads its own profile config from data-* attributes and recomputes on
   * quantity / variant changes. Registered once; guarded against re-definition.
   */
  if (!window.customElements.get('delivery-estimate')) {
    class DeliveryEstimate extends HTMLElement {
      connectedCallback() {
        this.controller = new AbortController();
        this.output = this.querySelector('[data-delivery-estimate-output]');

        this.charge = Number(this.dataset.charge) || 0;         // kr
        this.freeAbove = Number(this.dataset.freeAbove) || 0;   // kr
        this.unitPrice = Number(this.dataset.unitPrice) || 0;   // cents
        this.prefix = this.dataset.prefix || '';
        this.freeLabel = this.dataset.freeLabel || 'Gratis';

        this.bindEvents();
        this.render();
      }

      disconnectedCallback() {
        this.controller?.abort();
      }

      bindEvents() {
        const { signal } = this.controller;

        // Quantity: find the quantity input in the surrounding product form.
        this.form = this.closest('form') || this.closest('product-form');
        this.qtyInput =
          this.form?.querySelector('input[name="quantity"], quantity-selector input') || null;

        this.qtyInput?.addEventListener('change', () => this.render(), { signal });
        this.qtyInput?.addEventListener('input', () => this.render(), { signal });

        // Variant change: Prestige dispatches "variant:change" with the variant.
        document.addEventListener(
          'variant:change',
          (event) => {
            const variant = event.detail?.variant;
            if (variant && typeof variant.price === 'number') {
              this.unitPrice = variant.price;
              this.render();
            }
          },
          { signal }
        );
      }

      get quantity() {
        const value = parseInt(this.qtyInput?.value || '1', 10);
        return Number.isFinite(value) && value > 0 ? value : 1;
      }

      render() {
        if (!this.output) return;

        const lineTotal = this.unitPrice * this.quantity; // cents
        const freeCents = this.freeAbove * MINOR_UNITS;
        const isFree = this.freeAbove > 0 && lineTotal >= freeCents;

        const value = isFree ? this.freeLabel : formatMoney(this.charge * MINOR_UNITS);
        this.output.textContent = this.prefix ? `${this.prefix} ${value}` : value;
        this.dataset.state = isFree ? 'free' : 'charged';
      }
    }

    window.customElements.define('delivery-estimate', DeliveryEstimate);
  }
})();